// frontend/src/modules/search/CommandPalette.tsx
//
// Global search command palette (⌘K / Ctrl+K). Hand-rolled (cmdk is not a
// dependency): a centered modal with a single input, debounced /api/search
// calls, results grouped by type with lucide icons, and full keyboard nav
// (↑/↓ to move, Enter to open, Esc to close). Light + dark.

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Users, FileText, Package, User, FolderKanban, Receipt,
  CornerDownLeft, Loader2,
} from 'lucide-react';
import { api } from '../../core/api-client';
import { cn } from '../../lib/utils';

type SearchType = 'client' | 'invoice' | 'service' | 'employee' | 'project' | 'expense';

interface SearchResult {
  type: SearchType;
  id: string;
  label: string;
  sublabel: string | null;
  link: string;
}

const TYPE_META: Record<SearchType, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  client: { label: 'Clients', icon: Users },
  invoice: { label: 'Invoices', icon: FileText },
  service: { label: 'Services', icon: Package },
  employee: { label: 'People', icon: User },
  project: { label: 'Projects', icon: FolderKanban },
  expense: { label: 'Expenses', icon: Receipt },
};

// Stable grouping order in the dropdown.
const TYPE_ORDER: SearchType[] = ['client', 'invoice', 'project', 'employee', 'service', 'expense'];

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const [results, setResults] = React.useState<SearchResult[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const reqIdRef = React.useRef(0);

  // Reset + focus when opened.
  React.useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setActive(0);
      // Focus after the modal paints.
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Debounced fetch (~250ms). Ignores stale responses via a request id.
  React.useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const myReq = ++reqIdRef.current;
    const handle = setTimeout(async () => {
      try {
        const res = await api<{ results: SearchResult[] }>(`/api/search?q=${encodeURIComponent(q)}`);
        if (reqIdRef.current === myReq) {
          setResults(res.results ?? []);
          setActive(0);
        }
      } catch {
        if (reqIdRef.current === myReq) setResults([]);
      } finally {
        if (reqIdRef.current === myReq) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, open]);

  const go = React.useCallback((r: SearchResult) => {
    onClose();
    navigate(r.link);
  }, [navigate, onClose]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (results.length ? (i + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = results[active];
      if (r) go(r);
    }
  };

  // Build a flat-but-grouped render order so the active index maps to a row.
  const grouped = React.useMemo(() => {
    const byType = new Map<SearchType, SearchResult[]>();
    for (const r of results) {
      const arr = byType.get(r.type) ?? [];
      arr.push(r);
      byType.set(r.type, arr);
    }
    const flat: SearchResult[] = [];
    const sections: { type: SearchType; items: SearchResult[]; startIndex: number }[] = [];
    for (const t of TYPE_ORDER) {
      const items = byType.get(t);
      if (items && items.length) {
        sections.push({ type: t, items, startIndex: flat.length });
        flat.push(...items);
      }
    }
    return { flat, sections };
  }, [results]);

  if (!open) return null;

  const q = query.trim();

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Global search"
    >
      {/* Backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 bg-ink/30 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          'relative w-full max-w-xl overflow-hidden rounded-2xl shadow-2xl',
          'bg-white dark:bg-[#15151D] ring-1 ring-ink/10 dark:ring-white/10',
        )}
        onKeyDown={onKeyDown}
      >
        {/* Input row */}
        <div className="flex items-center gap-3 px-4 h-14 border-b border-ink/8 dark:border-white/10">
          {loading
            ? <Loader2 className="h-4 w-4 text-ink/40 dark:text-white/40 animate-spin" />
            : <Search className="h-4 w-4 text-ink/40 dark:text-white/40" />}
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search invoices, clients, people, projects…"
            className={cn(
              'flex-1 bg-transparent outline-none text-[15px]',
              'text-ink dark:text-white placeholder:text-ink/40 dark:placeholder:text-white/40',
            )}
          />
          <kbd className="text-[10px] font-semibold text-ink/50 dark:text-white/45 px-1.5 py-0.5 rounded-md bg-ink/5 dark:bg-white/[0.08] ring-1 ring-ink/10 dark:ring-white/10">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[55vh] overflow-y-auto py-2">
          {q.length < 2 ? (
            <p className="px-4 py-6 text-center text-[13px] text-ink/45 dark:text-white/40">
              Type at least 2 characters to search.
            </p>
          ) : !loading && grouped.flat.length === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-ink/45 dark:text-white/40">
              No results for “{q}”.
            </p>
          ) : (
            grouped.sections.map((section) => {
              const meta = TYPE_META[section.type];
              const SectionIcon = meta.icon;
              return (
                <div key={section.type} className="px-2">
                  <p className="px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-ink/40 dark:text-white/35">
                    {meta.label}
                  </p>
                  {section.items.map((r, i) => {
                    const idx = section.startIndex + i;
                    const isActive = idx === active;
                    return (
                      <button
                        key={`${r.type}-${r.id}`}
                        type="button"
                        onMouseEnter={() => setActive(idx)}
                        onClick={() => go(r)}
                        className={cn(
                          'w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left',
                          isActive ? 'bg-ink/5 dark:bg-white/[0.08]' : 'hover:bg-ink/[0.03] dark:hover:bg-white/[0.04]',
                        )}
                      >
                        <span className={cn(
                          'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                          'bg-ink/5 dark:bg-white/[0.06] text-ink/60 dark:text-white/60',
                        )}>
                          <SectionIcon className="h-3.5 w-3.5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[14px] text-ink dark:text-white">{r.label}</span>
                          {r.sublabel && (
                            <span className="block truncate text-[12px] text-ink/45 dark:text-white/40">{r.sublabel}</span>
                          )}
                        </span>
                        {isActive && (
                          <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-ink/35 dark:text-white/35" />
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
